import React, { useState, useCallback } from 'react';
import { accountingAPI } from '../../../services/api';
import { PageHeader }    from '../../../components/layout/PageHeader';
import { ArrowDownTrayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const fmt  = n => '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
const fmtK = n => { const v=Number(n||0); return v>=100000?'₹'+(v/100000).toFixed(2)+'L':v>=1000?'₹'+(v/1000).toFixed(2)+'K':fmt(v); };

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function PLRow({ label, value, bold, indent, green, red, borderTop, borderBottom }) {
  return (
    <tr className={`${borderTop?'border-t-2 border-gray-700':''} ${borderBottom?'border-b-2 border-gray-700':''}`}>
      <td className={`py-2.5 px-4 text-sm ${bold?'font-bold text-gray-900':'text-gray-600'} ${indent?'pl-10':''}`}>{label}</td>
      <td className={`py-2.5 px-4 text-sm text-right ${bold?'font-bold':'font-medium'} ${green?'text-green-700':red?'text-red-600':'text-gray-800'}`}>
        {value !== null && value !== undefined ? fmt(value) : '—'}
      </td>
    </tr>
  );
}

export function ProfitLossReport() {
  const thisYear   = new Date().getFullYear();
  const [start, setStart] = useState(`${thisYear}-04-01`);
  const [end,   setEnd]   = useState(`${thisYear}-03-31`);
  const [data,  setData]  = useState(null);
  const [loading, setLoad] = useState(false);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const res = await accountingAPI.getProfitLoss({ startDate: start, endDate: end });
      setData(res.data);
    } catch { toast.error('Failed to load P&L'); }
    finally { setLoad(false); }
  }, [start, end]);

  // Build totals from monthly data
  const totals = { sale:0, indirect_income:0, purchase:0, expense:0 };
  (data?.monthly||[]).forEach(m => {
    if (totals[m._id.type] !== undefined) totals[m._id.type] += m.total;
  });

  const grossProfit  = totals.sale - totals.purchase;
  const netProfit    = (totals.sale + totals.indirect_income) - (totals.purchase + totals.expense);
  const margin       = totals.sale > 0 ? ((netProfit/totals.sale)*100).toFixed(2) : 0;

  // Monthly breakdown map
  const monthMap = {};
  (data?.monthly||[]).forEach(m => {
    const key = `${m._id.year}-${m._id.month}`;
    if (!monthMap[key]) monthMap[key] = { label: MONTHS[m._id.month-1]+' '+m._id.year, sale:0, indirect_income:0, purchase:0, expense:0 };
    if (monthMap[key][m._id.type] !== undefined) monthMap[key][m._id.type] += m.total;
  });
  const monthRows = Object.values(monthMap);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Profit & Loss Report" subtitle="Income and expenditure statement for selected period" />

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex gap-4 flex-wrap items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">From Date</label>
            <input type="date" value={start} onChange={e=>setStart(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">To Date</label>
            <input type="date" value={end} onChange={e=>setEnd(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          {/* Quick ranges */}
          <div className="flex gap-2 flex-wrap">
            {[
              { label:'FY 25-26', s:`2025-04-01`, e:`2026-03-31` },
              { label:'FY 24-25', s:`2024-04-01`, e:`2025-03-31` },
              { label:'This Month', s: (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; })(),
                e: (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${new Date(d.getFullYear(),d.getMonth()+1,0).getDate()}`; })() },
            ].map(r => (
              <button key={r.label} onClick={()=>{setStart(r.s);setEnd(r.e);}}
                className="px-3 py-2 text-xs font-medium border border-gray-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-300 text-gray-600 transition-colors">
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
          {data && (
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
              <ArrowDownTrayIcon className="w-4 h-4"/> Print / PDF
            </button>
          )}
        </div>
      </div>

      {!data && !loading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <DocumentChartBarIcon className="w-12 h-12 mx-auto text-gray-200 mb-3"/>
          <p className="text-gray-400 text-sm">Select a date range and click Generate Report</p>
        </div>
      )}

      {data && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label:'Total Revenue',  value: fmtK(totals.sale+totals.indirect_income), color:'text-indigo-600' },
              { label:'Total Costs',    value: fmtK(totals.purchase+totals.expense),      color:'text-red-600'   },
              { label:'Gross Profit',   value: fmtK(grossProfit),  color: grossProfit>=0?'text-green-600':'text-red-600' },
              { label:'Net Profit',     value: fmtK(netProfit)+` (${margin}%)`, color: netProfit>=0?'text-green-600':'text-red-600' },
            ].map(k=>(
              <div key={k.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{k.label}</p>
                <p className={`text-xl font-bold mt-1 ${k.color}`}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* P&L Statement */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" id="pl-printable">
            <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between bg-gray-50">
              <div>
                <h2 className="font-bold text-gray-800">Profit & Loss Account</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  For the period {new Date(start).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})} to {new Date(end).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}
                </p>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-xs font-semibold text-gray-500 uppercase px-4 py-2.5 text-left w-2/3">Particulars</th>
                  <th className="text-xs font-semibold text-gray-500 uppercase px-4 py-2.5 text-right">Amount (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {/* INCOME */}
                <tr className="bg-green-50"><td colSpan={2} className="px-4 py-2 text-xs font-bold text-green-700 uppercase tracking-wide">A. Income</td></tr>
                <PLRow label="Sales / Revenue"       value={totals.sale}            indent />
                <PLRow label="Other Income"          value={totals.indirect_income} indent />
                <PLRow label="Total Income (A)"      value={totals.sale+totals.indirect_income} bold green borderTop />

                {/* EXPENSES */}
                <tr className="bg-red-50"><td colSpan={2} className="px-4 py-2 text-xs font-bold text-red-700 uppercase tracking-wide">B. Expenditure</td></tr>
                <PLRow label="Purchases / COGS"      value={totals.purchase} indent />
                <PLRow label="Operating Expenses"    value={totals.expense}  indent />
                <PLRow label="Total Expenditure (B)" value={totals.purchase+totals.expense} bold red borderTop />

                {/* PROFIT */}
                <PLRow label="Gross Profit (Sales − Purchases)" value={grossProfit} bold={false} green={grossProfit>=0} red={grossProfit<0} borderTop />
                <PLRow label="Net Profit / (Loss) (A − B)" value={netProfit} bold green={netProfit>=0} red={netProfit<0} borderTop borderBottom />
                <PLRow label={`Net Profit Margin`} value={null} bold={false} />
              </tbody>
            </table>
            <div className="px-4 pb-4 text-right">
              <span className={`text-sm font-bold ${netProfit>=0?'text-green-600':'text-red-600'}`}>{margin}%</span>
            </div>
          </div>

          {/* Monthly breakdown */}
          {monthRows.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-6 py-4 bg-gray-50">
                <h3 className="font-semibold text-gray-800 text-sm">Monthly Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Month','Revenue','Purchases','Expenses','Gross Profit','Net Profit'].map(h=>(
                        <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-right first:text-left">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {monthRows.map((m,i)=>{
                      const gp = m.sale - m.purchase;
                      const np = m.sale + m.indirect_income - m.purchase - m.expense;
                      return (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2.5 font-medium text-gray-800">{m.label}</td>
                          <td className="px-4 py-2.5 text-right text-indigo-600 font-medium">{fmt(m.sale+m.indirect_income)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-600">{fmt(m.purchase)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-600">{fmt(m.expense)}</td>
                          <td className={`px-4 py-2.5 text-right font-medium ${gp>=0?'text-green-600':'text-red-600'}`}>{fmt(gp)}</td>
                          <td className={`px-4 py-2.5 text-right font-bold ${np>=0?'text-green-600':'text-red-600'}`}>{fmt(np)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold">
                    <tr>
                      <td className="px-4 py-2.5 text-gray-800">TOTAL</td>
                      <td className="px-4 py-2.5 text-right text-indigo-600">{fmt(totals.sale+totals.indirect_income)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-800">{fmt(totals.purchase)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-800">{fmt(totals.expense)}</td>
                      <td className={`px-4 py-2.5 text-right ${grossProfit>=0?'text-green-600':'text-red-600'}`}>{fmt(grossProfit)}</td>
                      <td className={`px-4 py-2.5 text-right ${netProfit>=0?'text-green-600':'text-red-600'}`}>{fmt(netProfit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Top customers */}
          {(data.topCustomers||[]).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-6 py-4 bg-gray-50">
                <h3 className="font-semibold text-gray-800 text-sm">Top Customers by Revenue</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['#','Customer','Invoices','Revenue'].map(h=>(
                      <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-left last:text-right">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.topCustomers.map((c,i)=>(
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-400 text-xs">{i+1}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{c.name||'Unknown'}</td>
                      <td className="px-4 py-2.5 text-gray-500">{c.count}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-indigo-600">{fmt(c.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}